// components/common/Alert/Alert.tsx
"use client";

import React, { useEffect } from "react";
import "./Alert.css";
// Use lucide-react icons (keeps behavior identical; only visuals change)
import { CheckCircle, XCircle, AlertTriangle, Info, FileText } from "lucide-react";

type AlertType = "success" | "error" | "warning" | "info" | "detail";

interface AlertProps {
  type?: AlertType;
  children: React.ReactNode;
  className?: string;
  closable?: boolean;
  onClose?: () => void;
  autoCloseDuration?: number; // in milliseconds
}

// Map alert types to lucide icons (no behavior change, just visuals)
const iconMap: Record<AlertType, JSX.Element> = {
  success: <CheckCircle aria-hidden="true" />,
  error:   <XCircle aria-hidden="true" />,
  warning: <AlertTriangle aria-hidden="true" />,
  info:    <Info aria-hidden="true" />,
  detail:  <FileText aria-hidden="true" />,
};

const Alert: React.FC<AlertProps> = ({
  type = "info",
  children,
  className = "",
  closable = false,
  onClose,
  autoCloseDuration,
}) => {
  // Auto-close effect
  useEffect(() => {
    if (autoCloseDuration && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDuration);
      return () => clearTimeout(timer);
    }
  }, [autoCloseDuration, onClose]);

  return (
    <div className={`alert alert-${type} ${className}`}>
      <span className="alert-icon">{iconMap[type]} </span>
      <span className="alert-content">{children}</span>
      {closable && (
        <button className="alert-close" onClick={onClose} aria-label="Close alert">
          ×
        </button>
      )}
    </div>
  );
};

export default Alert;
